import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../../app/store';
import { postDirectSignup } from '../../api/hattersAPI';

interface EmailSignup {
  email: string;
  newsletterOptin: boolean;
}
export enum DirectSignupState {
  idle = 'directSignup/idle',
  processing = 'directSignup/processing',
  successful = 'directSignup/successful',
  failed = 'directSignup/failed',
}

export interface IDirectSignupState {
  step: number;
  email: string;
  username: string;
  password: string;
  newsletterOptin: boolean;
  termsAgreed: boolean;
  signupState: DirectSignupState;
  nextStep: string;
}

const initialState: IDirectSignupState = {
  step: 0,
  email: '',
  username: '',
  password: '',
  newsletterOptin: false,
  termsAgreed: false,
  signupState: DirectSignupState.idle,
  nextStep: '',
};

export const slice = createSlice({
  name: 'directSignup',
  initialState: Object.assign({}, initialState),
  reducers: {
    updateSignupState: (state, action: PayloadAction<DirectSignupState>) => {
      state.signupState = action.payload;
    },
    updateEmailSubs: (state, action: PayloadAction<EmailSignup>) => {
      state.email = action.payload.email;
      state.newsletterOptin = action.payload.newsletterOptin;
      state.step = 1;
    },
    updateUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
      state.step = 2;
    },
    updatePassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
      state.step = 3;
    },
    updateTermsAgreed: (state) => {
      state.termsAgreed = true;
      state.signupState = DirectSignupState.processing;
    },
    updateSignupNextStep: (state, action: PayloadAction<string>) => {
      state.nextStep = action.payload;
    },
    resetState: (state) => {
      Object.assign(state, {}, initialState);
    },
  },
});

export const {
  updateEmailSubs,
  updateUsername,
  updatePassword,
  updateTermsAgreed,
  updateSignupState,
  updateSignupNextStep,
  resetState,
} = slice.actions;

export const setDirectSignupEmailSubs =
  (payload: EmailSignup): AppThunk =>
    (dispatch) => {
      dispatch(updateEmailSubs(payload));
    };
export const setDirectSignupUsername =
  (payload: string): AppThunk =>
    (dispatch) => {
      dispatch(updateUsername(payload));
    };
export const setDirectSignupPassword =
  (payload: string): AppThunk =>
    (dispatch) => {
      dispatch(updatePassword(payload));
    };
export const setDirectSignupTermsAgreed = (): AppThunk => (dispatch) => {
  dispatch(updateTermsAgreed());
};
export const setDirectSignupState =
  (payload: DirectSignupState): AppThunk =>
    (dispatch) => {
      dispatch(updateSignupState(payload));
    };
export const setDirectSignupNextStep =
  (payload: string): AppThunk =>
    (dispatch) => {
      dispatch(updateSignupNextStep(payload));
    };

export const resetDirectSignupState = (): AppThunk => (dispatch) => {
  dispatch(resetState());
};
export const selectDirectSignup = (state: RootState) => state.directSignup;
export const selectDirectSignupStep = (state: RootState) => state.directSignup.step;
export const selectDirectSignupState = (state: RootState) => state.directSignup.signupState;

export const createDirectSignup =
  (signup: IDirectSignupState, sandbox: boolean): AppThunk =>
    async (dispatch) => {
      try {
        const res = await postDirectSignup(signup, sandbox);
        if (res.parsedBody && res.parsedBody.nextStep) {
          dispatch(setDirectSignupState(DirectSignupState.successful));
          dispatch(setDirectSignupNextStep(res.parsedBody.nextStep));
        } else {
          dispatch(setDirectSignupState(DirectSignupState.failed));
        }
      } catch (e) {
        dispatch(setDirectSignupState(DirectSignupState.failed));
      }
    };

export default slice.reducer;
